/* ==========================================================================
   MX theme – mx-ask.js   <mx-ask>
   The "ask the way you'd say it" search box used by the MX Ask search section and the search page.

     • Understands budgets and price words through MXTheme.search.parse (mx-global.js): "RTX 5070 under 3 lakh"
       searches "RTX 5070" with a real price filter of up to Rs 300,000. The line under the box shows what was
       understood before the shopper presses Enter.
     • Suggests products while typing (Shopify's predictive search, /search/suggest.json), keeping only those
       inside the budget. Combobox pattern: arrow keys move through the list, Enter opens the highlighted
       product or searches, Escape closes.
     • An animated placeholder cycles through example searches (not with reduced motion, not while focused).
   Without JavaScript it is a plain search form to /search.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('mx-ask')) return;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function fill(t, v) { return String(t || '').replace(/\[(\w+)\]/g, function (m, k) { return k in v ? v[k] : m; }); }
  function money(rupees) { return window.MXTheme && MXTheme.money ? MXTheme.money(Math.round(rupees * 100)) : String(rupees); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  class MXAsk extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      var self = this;
      this.cleanup = [];
      this.timers = {};
      try { this.cfg = JSON.parse((this.querySelector('[data-mxa-config]') || {}).textContent || '{}'); } catch (e) { this.cfg = {}; }
      this.labels = this.cfg.labels || {};
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-mxa-input]');
      this.pop = this.querySelector('[data-mxa-pop]');
      this.list = this.querySelector('[data-mxa-list]');
      this.understood = this.querySelector('[data-mxa-understood]');
      this.allLink = this.querySelector('[data-mxa-all]');
      this.status = this.querySelector('[data-mxa-status]');
      this.clearBtn = this.querySelector('[data-mxa-clear]');
      if (!this.input || !this.form) return;
      this.classList.add('is-ready');
      this.active = -1;
      this.items = [];
      this.seq = 0;
      this.on(this.input, 'input', function () { self.changed(); });
      this.on(this.input, 'focus', function () { self.stopTyping(true); if (self.input.value.trim()) self.changed(); });
      this.on(this.input, 'keydown', function (e) { self.keydown(e); });
      this.on(this, 'focusout', function (e) { if (!self.contains(e.relatedTarget)) self.close(); });
      this.on(document, 'pointerdown', function (e) { if (!self.contains(e.target)) self.close(); });
      this.on(this.list, 'mousemove', function (e) { var li = e.target.closest('[role="option"]'); if (li) self.highlight(Number(li.getAttribute('data-i')), false); });
      this.on(this.list, 'mousedown', function (e) { if (e.target.closest('[role="option"]')) e.preventDefault(); });
      if (this.clearBtn) this.on(this.clearBtn, 'click', function () { self.input.value = ''; self.changed(); self.input.focus(); });
      if (this.allLink) this.on(this.allLink, 'click', function (e) { e.preventDefault(); self.submit(); });
      /* a starting value (the search page passes the shopper's own words) */
      if (this.cfg.value && !this.input.value) this.input.value = this.cfg.value;
      this.syncClear();
      this.initTyping();
    }
    disconnectedCallback() {
      this.started = false;
      (this.cleanup || []).forEach(function (fn) { fn(); });
      Object.keys(this.timers || {}).forEach(function (k) { clearTimeout(this.timers[k]); }, this);
      if (this.abort) { try { this.abort.abort(); } catch (e) { /* noop */ } }
      if (this.io) this.io.disconnect();
    }
    on(t, type, fn, o) { if (!t) return; t.addEventListener(type, fn, o); this.cleanup.push(function () { t.removeEventListener(type, fn, o); }); }
    later(name, fn, ms) { clearTimeout(this.timers[name]); this.timers[name] = setTimeout(fn, ms); }

    /* ------------------------------------------------------------ typing */
    syncClear() { if (this.clearBtn) this.clearBtn.hidden = !this.input.value; }
    changed() {
      var self = this;
      this.syncClear();
      var text = this.input.value.trim();
      if (!text) { this.close(); return; }
      var q = window.MXTheme && MXTheme.search ? MXTheme.search.parse(text) : { terms: text, min: null, max: null, sort: null };
      this.query = q;
      this.showUnderstood(q, text);
      if (this.allLink) this.allLink.textContent = fill(this.labels.all || 'See all results for “[q]”', { q: text });
      if (!q.terms || q.terms.length < 2) { this.render([], q); this.open(); return; }
      this.later('fetch', function () { self.fetch(q); }, 180);
    }
    showUnderstood(q, text) {
      if (!this.understood) return;
      var bits = [];
      if (q.min !== null && q.max !== null) bits.push(fill(this.labels.range || '[min] – [max]', { min: money(q.min), max: money(q.max) }));
      else if (q.max !== null) bits.push(fill(this.labels.max || 'up to [price]', { price: money(q.max) }));
      else if (q.min !== null) bits.push(fill(this.labels.min || 'from [price]', { price: money(q.min) }));
      if (q.sort === 'price-ascending') bits.push(this.labels.cheap || 'cheapest first');
      if (q.sort === 'price-descending') bits.push(this.labels.premium || 'highest price first');
      if (!bits.length) { this.understood.hidden = true; this.understood.innerHTML = ''; return; }
      var head = q.terms ? '<b>' + esc(q.terms) + '</b>' : esc(this.labels.everything || 'Everything');
      this.understood.innerHTML = '<span class="mxa__u-label">' + esc(this.labels.understood || 'Searching for') + '</span> ' + head +
        bits.map(function (b) { return ' <span class="mxa__u-chip">' + esc(b) + '</span>'; }).join('');
      this.understood.hidden = false;
    }
    fetch(q) {
      var self = this;
      var seq = ++this.seq;
      if (this.abort) { try { this.abort.abort(); } catch (e) { /* noop */ } }
      this.abort = 'AbortController' in window ? new AbortController() : null;
      var base = (window.MXTheme && MXTheme.config && MXTheme.config.routes && MXTheme.config.routes.predictiveSearch) || '/search/suggest';
      var url = base + '.json?q=' + encodeURIComponent(q.terms) + '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag';
      this.classList.add('is-loading');
      this.open();
      window.fetch(url, { credentials: 'same-origin', signal: this.abort ? this.abort.signal : undefined, headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          if (seq !== self.seq) return;
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          /* keep what fits the budget the shopper gave */
          products = products.filter(function (p) {
            var price = parseFloat(p.price_min || p.price);
            if (isNaN(price)) return true;
            if (q.max !== null && price > q.max) return false;
            if (q.min !== null && price < q.min) return false;
            return true;
          });
          if (q.sort === 'price-ascending') products.sort(function (a, b) { return parseFloat(a.price_min || a.price) - parseFloat(b.price_min || b.price); });
          if (q.sort === 'price-descending') products.sort(function (a, b) { return parseFloat(b.price_min || b.price) - parseFloat(a.price_min || a.price); });
          self.render(products.slice(0, self.cfg.limit || 6), q);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (seq === self.seq) self.render([], q);
        })
        .then(function () { if (seq === self.seq) self.classList.remove('is-loading'); });
    }
    render(products, q) {
      var self = this;
      this.items = products;
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      var id = this.list.id;
      if (!products.length) {
        this.list.innerHTML = q && q.terms && q.terms.length >= 2 ? '<li class="mxa__none" role="presentation">' + esc(this.labels.none || 'No quick matches. Press Enter to search everything.') + '</li>' : '';
      } else {
        this.list.innerHTML = products.map(function (p, i) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          var price = parseFloat(p.price_min || p.price);
          var from = parseFloat(p.price_max) > price ? (self.labels.from || 'From [price]') : '[price]';
          return '<li class="mxa__opt" role="option" id="' + id + '-' + i + '" data-i="' + i + '" aria-selected="false">' +
            '<a href="' + esc(p.url) + '" tabindex="-1">' +
            '<span class="mxa__thumb">' + (img ? '<img src="' + esc(img) + (img.indexOf('?') > -1 ? '&' : '?') + 'width=120" alt="" width="60" height="60" loading="lazy">' : '') + '</span>' +
            '<span class="mxa__meta"><span class="mxa__vendor">' + esc(p.vendor || '') + '</span><span class="mxa__title">' + esc(p.title) + '</span></span>' +
            '<span class="mxa__price">' + esc(fill(from, { price: isNaN(price) ? '' : money(price) })) + '</span>' +
            '</a></li>';
        }).join('');
      }
      if (this.status) this.status.textContent = products.length ? fill(this.labels.count || '[count] suggestions', { count: products.length }) : '';
      this.open();
    }
    open() {
      if (!this.pop) return;
      var has = this.list.children.length || (this.understood && !this.understood.hidden) || this.classList.contains('is-loading');
      this.pop.hidden = !has;
      this.input.setAttribute('aria-expanded', has ? 'true' : 'false');
      this.classList.toggle('is-open', !!has);
    }
    close() {
      if (!this.pop) return;
      this.pop.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }
    highlight(i, scroll) {
      var opts = this.list.querySelectorAll('[role="option"]');
      if (!opts.length) return;
      if (i < -1) i = opts.length - 1;
      if (i >= opts.length) i = -1;
      this.active = i;
      opts.forEach(function (o, k) { o.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
      if (i === -1) { this.input.removeAttribute('aria-activedescendant'); return; }
      this.input.setAttribute('aria-activedescendant', opts[i].id);
      if (scroll !== false) opts[i].scrollIntoView({ block: 'nearest' });
    }
    keydown(e) {
      var open = this.pop && !this.pop.hidden;
      if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) this.changed(); this.highlight(this.active + 1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); this.highlight(this.active - 1); return; }
      if (e.key === 'Escape') { if (open) { e.preventDefault(); this.close(); } else if (this.input.value) { this.input.value = ''; this.syncClear(); } return; }
      if (e.key === 'Enter' && open && this.active > -1) {
        e.preventDefault();
        var a = this.list.querySelector('#' + this.list.id + '-' + this.active + ' a');
        if (a) window.location.assign(a.getAttribute('href'));
      }
    }
    submit() {
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })) && this.form.submit();
    }

    /* ------------------------------------------------------------ animated placeholder */
    initTyping() {
      var self = this;
      var ex = this.cfg.examples || [];
      this.phPrefix = this.cfg.prefix || 'Try “[q]”';
      if (!ex.length) return;
      if (reduceMotion.matches || !('IntersectionObserver' in window)) { this.input.placeholder = fill(this.phPrefix, { q: ex[0] }); return; }
      this.ex = ex; this.exI = 0; this.exC = 0; this.dir = 1;
      this.io = new IntersectionObserver(function (en) { en.forEach(function (x) { self.visible = x.isIntersecting; if (x.isIntersecting) self.tick(); }); });
      this.io.observe(this);
    }
    stopTyping(forGood) {
      if (forGood) this.typingOff = true;
      clearTimeout(this.timers.type);
      if (this.ex) this.input.placeholder = fill(this.phPrefix, { q: this.ex[this.exI] });
    }
    tick() {
      var self = this;
      if (this.typingOff || !this.visible || document.activeElement === this.input || this.input.value) return;
      var word = this.ex[this.exI];
      this.exC += this.dir;
      var shown = word.slice(0, Math.max(0, this.exC));
      var open = this.phPrefix.split('[q]')[0], close = this.phPrefix.split('[q]')[1] || '';
      /* the closing quote only once the example is fully typed; between examples the box shows the prompt word alone */
      this.input.placeholder = shown ? open + shown + (this.exC >= word.length ? close : '') : open.replace(/[\s“"']+$/, '');
      var wait = this.dir > 0 ? 55 : 22;
      if (this.dir > 0 && this.exC >= word.length) { this.dir = -1; wait = 1800; }
      else if (this.dir < 0 && this.exC <= 0) { this.dir = 1; this.exI = (this.exI + 1) % this.ex.length; wait = 350; }
      this.later('type', function () { self.tick(); }, wait);
    }
  }
  customElements.define('mx-ask', MXAsk);
})();

/* <mx-ask-hero>: suggestion tags search through the budget parser; the entrance plays once in view. */
(function () {
  'use strict';
  if (customElements.get('mx-ask-hero')) return;
  class MXAskHero extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.onClick = function (e) {
        var a = e.target.closest('[data-mxs-ask]');
        if (!a || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (!(window.MXTheme && MXTheme.search)) return;
        e.preventDefault();
        window.location.assign(MXTheme.search.url(a.getAttribute('data-mxs-ask')));
      };
      this.addEventListener('click', this.onClick);
      if (!this.classList.contains('mxs--armed')) return;
      this.classList.add('mxs--live');
      if (window.Shopify && window.Shopify.designMode) { this.classList.add('is-in'); return; }
      this.io = new IntersectionObserver(function (en) {
        en.forEach(function (x) { if (x.isIntersecting) { self.classList.add('is-in'); self.io.disconnect(); } });
      }, { threshold: 0.2 });
      this.io.observe(this);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.io) this.io.disconnect();
    }
  }
  customElements.define('mx-ask-hero', MXAskHero);
})();
